import React, { useState, useEffect, useCallback } from 'react';
import {
  Building, Phone, MapPin, Clock, Globe, ChevronRight, ChevronLeft,
  Check, AlertCircle, Loader2, Lock, CheckCircle, ArrowRight,
  Calendar, MessageSquare, Shield, AlertTriangle, Sparkles, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getApiKey = () => localStorage.getItem('customer_api_key');

// ============================================================================
// MAIN ONBOARDING WIZARD COMPONENT
// ============================================================================

export const OnboardingWizard = ({ onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [wizardState, setWizardState] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [industries, setIndustries] = useState({});
  const [callObjectives, setCallObjectives] = useState([]);
  const [toneOptions, setToneOptions] = useState([]);

  // Form data for each step
  const [formData, setFormData] = useState({
    business_profile: {
      legal_name: '',
      display_name: '',
      website: '',
      primary_phone: '',
      address: '',
      city: '',
      state: '',
      country: 'United States',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      business_hours: {
        monday: { open: '09:00', close: '17:00', closed: false },
        tuesday: { open: '09:00', close: '17:00', closed: false },
        wednesday: { open: '09:00', close: '17:00', closed: false },
        thursday: { open: '09:00', close: '17:00', closed: false },
        friday: { open: '09:00', close: '17:00', closed: false },
        saturday: { open: '10:00', close: '14:00', closed: false },
        sunday: { open: '00:00', close: '00:00', closed: true }
      },
      after_hours_behavior: 'ai'
    },
    industry_selection: {
      primary_industry: '',
      secondary_industry: '',
      other_description: ''
    },
    call_objectives: {
      objectives: []
    },
    ai_permissions: {
      can_book_appointments: false,
      can_collect_name: true,
      can_collect_phone: true,
      can_collect_email: false,
      can_quote_prices: false,
      price_disclaimer: '',
      can_provide_advice: false,
      can_escalate_to_human: true,
      can_send_sms: false
    },
    appointment_details: {
      appointment_types: [{ name: '', duration_minutes: 30 }],
      max_per_day: 10,
      same_day_allowed: true,
      confirmation_message: '',
      cancellation_policy: ''
    },
    emergency_handling: {
      emergency_definition: '',
      emergency_phone: '',
      fully_booked_message: '',
      outside_hours_message: '',
      unsure_response: '',
      fallback_message: ''
    },
    brand_voice: {
      tone: 'professional',
      greeting_message: '',
      forbidden_phrases: []
    }
  });

  // Load wizard state and industries
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [wizardRes, industriesRes] = await Promise.all([
        fetch(`${API_URL}/api/onboarding/wizard`, {
          headers: { 'X-API-Key': getApiKey() }
        }),
        fetch(`${API_URL}/api/onboarding/industries`)
      ]);

      if (wizardRes.ok) {
        const wizard = await wizardRes.json();
        setWizardState(wizard);
        setCurrentStep(wizard.current_step || 1);
        
        // Populate form data from saved state
        if (wizard.business_profile) {
          setFormData(prev => ({ ...prev, business_profile: { ...prev.business_profile, ...wizard.business_profile } }));
        }
        if (wizard.industry_selection) {
          setFormData(prev => ({ ...prev, industry_selection: { ...prev.industry_selection, ...wizard.industry_selection } }));
        }
        if (wizard.call_objectives) {
          setFormData(prev => ({ ...prev, call_objectives: { ...prev.call_objectives, ...wizard.call_objectives } }));
        }
        if (wizard.ai_permissions) {
          setFormData(prev => ({ ...prev, ai_permissions: { ...prev.ai_permissions, ...wizard.ai_permissions } }));
        }
        if (wizard.appointment_details) {
          setFormData(prev => ({ ...prev, appointment_details: { ...prev.appointment_details, ...wizard.appointment_details } }));
        }
        if (wizard.emergency_handling) {
          setFormData(prev => ({ ...prev, emergency_handling: { ...prev.emergency_handling, ...wizard.emergency_handling } }));
        }
        if (wizard.brand_voice) {
          setFormData(prev => ({ ...prev, brand_voice: { ...prev.brand_voice, ...wizard.brand_voice } }));
        }
      }

      if (industriesRes.ok) {
        const data = await industriesRes.json();
        setIndustries(data.industries);
        setCallObjectives(data.call_objectives);
        setToneOptions(data.tone_options);
      }
    } catch (err) {
      setError('Failed to load wizard data');
    } finally {
      setLoading(false);
    }
  };

  const saveStep = async (stepNumber, data) => {
    setSaving(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/onboarding/wizard/step/${stepNumber}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getApiKey()
        },
        body: JSON.stringify(data)
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.detail || 'Failed to save');
      }

      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    // Get current step data
    const stepDataMap = {
      1: formData.business_profile,
      2: formData.industry_selection,
      3: formData.call_objectives,
      4: formData.ai_permissions,
      5: formData.appointment_details,
      6: formData.emergency_handling,
      7: formData.brand_voice
    };

    // Validate current step
    const validation = validateStep(currentStep);
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    // Save current step
    const saved = await saveStep(currentStep, stepDataMap[currentStep]);
    if (saved) {
      // Skip step 5 if booking not enabled
      if (currentStep === 4 && !formData.ai_permissions.can_book_appointments) {
        setCurrentStep(6);
      } else if (currentStep < 8) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Skip step 5 going back if booking not enabled
      if (currentStep === 6 && !formData.ai_permissions.can_book_appointments) {
        setCurrentStep(4);
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/onboarding/wizard/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getApiKey()
        }
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.detail || 'Failed to submit');
      }

      // Notify parent component
      if (onComplete) {
        onComplete(result);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        const bp = formData.business_profile;
        if (!bp.legal_name) return { valid: false, message: 'Business legal name is required' };
        if (!bp.display_name) return { valid: false, message: 'Business display name is required' };
        if (!bp.primary_phone) return { valid: false, message: 'Primary phone number is required' };
        if (!bp.city) return { valid: false, message: 'City is required' };
        if (!bp.state) return { valid: false, message: 'State is required' };
        break;
      case 2:
        const ind = formData.industry_selection;
        if (!ind.primary_industry) return { valid: false, message: 'Please select your industry' };
        if (!ind.secondary_industry) return { valid: false, message: 'Please select your specific business type' };
        if (ind.secondary_industry === 'Other' && !ind.other_description) {
          return { valid: false, message: 'Please describe your business' };
        }
        break;
      case 3:
        if (formData.call_objectives.objectives.length === 0) {
          return { valid: false, message: 'Please select at least one call objective' };
        }
        break;
      case 6:
        const em = formData.emergency_handling;
        if (!em.fallback_message) return { valid: false, message: 'Fallback message is required' };
        break;
      case 7:
        const bv = formData.brand_voice;
        if (!bv.greeting_message) return { valid: false, message: 'Greeting message is required' };
        break;
    }
    return { valid: true };
  };

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Check if wizard is submitted/locked
  if (wizardState?.wizard_status === 'submitted' || wizardState?.wizard_status === 'locked') {
    return <WizardSubmittedView wizardState={wizardState} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your setup wizard...</p>
        </div>
      </div>
    );
  }

  const steps = [
    { num: 1, title: 'Business Profile', icon: Building },
    { num: 2, title: 'Industry', icon: Globe },
    { num: 3, title: 'Call Objectives', icon: Phone },
    { num: 4, title: 'AI Permissions', icon: Shield },
    { num: 5, title: 'Appointments', icon: Calendar, skip: !formData.ai_permissions.can_book_appointments },
    { num: 6, title: 'Emergencies', icon: AlertTriangle },
    { num: 7, title: 'Brand Voice', icon: MessageSquare },
    { num: 8, title: 'Review', icon: CheckCircle }
  ].filter(s => !s.skip);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">AI Agent Setup Wizard</h1>
              <p className="text-sm text-gray-500">Complete all steps to activate your AI phone agent</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Lock className="w-4 h-4" />
              Auto-saving
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <React.Fragment key={step.num}>
                <div 
                  className={`flex items-center gap-2 ${
                    currentStep === step.num ? 'text-blue-600' :
                    currentStep > step.num ? 'text-green-600' : 'text-gray-400'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === step.num ? 'bg-blue-600 text-white' :
                    currentStep > step.num ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.num ? <Check className="w-4 h-4" /> : step.num}
                  </div>
                  <span className="hidden md:block text-sm font-medium">{step.title}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    currentStep > step.num ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Step Content */}
          {currentStep === 1 && (
            <Step1BusinessProfile 
              data={formData.business_profile} 
              onChange={(field, value) => updateFormData('business_profile', field, value)}
            />
          )}
          {currentStep === 2 && (
            <Step2Industry 
              data={formData.industry_selection}
              industries={industries}
              onChange={(field, value) => updateFormData('industry_selection', field, value)}
            />
          )}
          {currentStep === 3 && (
            <Step3CallObjectives
              data={formData.call_objectives}
              objectives={callObjectives}
              onChange={(field, value) => updateFormData('call_objectives', field, value)}
            />
          )}
          {currentStep === 4 && (
            <Step4AIPermissions
              data={formData.ai_permissions}
              onChange={(field, value) => updateFormData('ai_permissions', field, value)}
            />
          )}
          {currentStep === 5 && formData.ai_permissions.can_book_appointments && (
            <Step5AppointmentDetails
              data={formData.appointment_details}
              onChange={(field, value) => updateFormData('appointment_details', field, value)}
            />
          )}
          {currentStep === 6 && (
            <Step6EmergencyHandling
              data={formData.emergency_handling}
              onChange={(field, value) => updateFormData('emergency_handling', field, value)}
            />
          )}
          {currentStep === 7 && (
            <Step7BrandVoice
              data={formData.brand_voice}
              toneOptions={toneOptions}
              onChange={(field, value) => updateFormData('brand_voice', field, value)}
            />
          )}
          {currentStep === 8 && (
            <Step8Review
              formData={formData}
              industries={industries}
              callObjectives={callObjectives}
              toneOptions={toneOptions}
            />
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleBack}
              disabled={currentStep === 1 || saving}
              className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>

            {currentStep < 8 ? (
              <button
                onClick={handleNext}
                disabled={saving}
                className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit & Activate
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STEP COMPONENTS
// ============================================================================

const Step1BusinessProfile = ({ data, onChange }) => {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  
  const updateHours = (day, field, value) => {
    onChange('business_hours', {
      ...data.business_hours,
      [day]: { ...data.business_hours[day], [field]: value }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Business Profile</h2>
        <p className="text-gray-500">Tell us about your business so we can personalize your AI agent.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Business Legal Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.legal_name}
            onChange={(e) => onChange('legal_name', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="ABC Dental Clinic LLC"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.display_name}
            onChange={(e) => onChange('display_name', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="ABC Dental"
          />
          <p className="text-xs text-gray-500 mt-1">How callers will hear your business name</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Primary Phone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={data.primary_phone}
            onChange={(e) => onChange('primary_phone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="(555) 123-4567"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
          <input
            type="url"
            value={data.website}
            onChange={(e) => onChange('website', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="https://www.example.com"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <input
            type="text"
            value={data.address}
            onChange={(e) => onChange('address', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="123 Main Street, Suite 100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.city}
            onChange={(e) => onChange('city', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="New York"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.state}
            onChange={(e) => onChange('state', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="NY"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
          <select
            value={data.country}
            onChange={(e) => onChange('country', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="United States">United States</option>
            <option value="Canada">Canada</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
          <select
            value={data.timezone}
            onChange={(e) => onChange('timezone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="America/Phoenix">Arizona (MST)</option>
          </select>
        </div>
      </div>

      {/* Business Hours */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Clock className="w-4 h-4 inline mr-1" />
          Business Hours
        </label>
        <div className="space-y-2">
          {days.map(day => (
            <div key={day} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <span className="w-28 font-medium text-gray-700 capitalize">{day}</span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!data.business_hours[day]?.closed}
                  onChange={(e) => updateHours(day, 'closed', !e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm">Open</span>
              </label>
              {!data.business_hours[day]?.closed && (
                <>
                  <input
                    type="time"
                    value={data.business_hours[day]?.open || '09:00'}
                    onChange={(e) => updateHours(day, 'open', e.target.value)}
                    className="px-3 py-1 border rounded text-sm"
                  />
                  <span>to</span>
                  <input
                    type="time"
                    value={data.business_hours[day]?.close || '17:00'}
                    onChange={(e) => updateHours(day, 'close', e.target.value)}
                    className="px-3 py-1 border rounded text-sm"
                  />
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* After Hours Behavior */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          After-Hours Behavior
        </label>
        <div className="grid grid-cols-3 gap-4">
          {[
            { id: 'voicemail', label: 'Voicemail', desc: 'Send to voicemail' },
            { id: 'ai', label: 'AI Answers', desc: 'AI handles calls' },
            { id: 'emergency_only', label: 'Emergency Only', desc: 'Only urgent calls' }
          ].map(opt => (
            <label
              key={opt.id}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                data.after_hours_behavior === opt.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="after_hours"
                value={opt.id}
                checked={data.after_hours_behavior === opt.id}
                onChange={(e) => onChange('after_hours_behavior', e.target.value)}
                className="sr-only"
              />
              <div className="font-medium text-gray-900">{opt.label}</div>
              <div className="text-sm text-gray-500">{opt.desc}</div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

const Step2Industry = ({ data, industries, onChange }) => {
  const selectedIndustry = industries[data.primary_industry];
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Industry Selection</h2>
        <p className="text-gray-500">This helps us customize your AI agent's knowledge and responses.</p>
      </div>

      {/* Primary Industry */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          What industry is your business in? <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(industries).map(([key, ind]) => (
            <label
              key={key}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all text-center ${
                data.primary_industry === key
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="primary_industry"
                value={key}
                checked={data.primary_industry === key}
                onChange={(e) => {
                  onChange('primary_industry', e.target.value);
                  onChange('secondary_industry', '');
                }}
                className="sr-only"
              />
              <div className="text-2xl mb-1">{ind.icon}</div>
              <div className="font-medium text-gray-900 text-sm">{ind.name}</div>
            </label>
          ))}
        </div>
      </div>

      {/* Secondary Industry */}
      {selectedIndustry && selectedIndustry.subcategories.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            What type of {selectedIndustry.name.toLowerCase()} business? <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {selectedIndustry.subcategories.map(sub => (
              <label
                key={sub}
                className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  data.secondary_industry === sub
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="secondary_industry"
                  value={sub}
                  checked={data.secondary_industry === sub}
                  onChange={(e) => onChange('secondary_industry', e.target.value)}
                  className="sr-only"
                />
                <div className="font-medium text-gray-900 text-sm">{sub}</div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Other Description */}
      {data.secondary_industry === 'Other' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Please describe your business <span className="text-red-500">*</span>
          </label>
          <textarea
            value={data.other_description}
            onChange={(e) => onChange('other_description', e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Briefly describe what your business does..."
          />
        </div>
      )}
    </div>
  );
};

const Step3CallObjectives = ({ data, objectives, onChange }) => {
  const toggleObjective = (id) => {
    const current = data.objectives || [];
    if (current.includes(id)) {
      onChange('objectives', current.filter(o => o !== id));
    } else {
      onChange('objectives', [...current, id]);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Call Objectives</h2>
        <p className="text-gray-500">What should your AI agent do when handling calls? Select all that apply.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {objectives.map(obj => (
          <label
            key={obj.id}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all flex items-start gap-3 ${
              data.objectives?.includes(obj.id)
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="checkbox"
              checked={data.objectives?.includes(obj.id)}
              onChange={() => toggleObjective(obj.id)}
              className="w-5 h-5 text-blue-600 rounded mt-0.5"
            />
            <div>
              <div className="font-medium text-gray-900 flex items-center gap-2">
                <span>{obj.icon}</span>
                {obj.label}
              </div>
            </div>
          </label>
        ))}
      </div>

      {data.objectives?.length > 0 && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{data.objectives.length} objective{data.objectives.length > 1 ? 's' : ''} selected</span>
          </div>
        </div>
      )}
    </div>
  );
};

const Step4AIPermissions = ({ data, onChange }) => {
  const permissions = [
    { id: 'can_book_appointments', label: 'Can AI book appointments automatically?', important: true },
    { id: 'can_collect_name', label: 'Can AI collect caller name?' },
    { id: 'can_collect_phone', label: 'Can AI collect phone number?' },
    { id: 'can_collect_email', label: 'Can AI collect email address?' },
    { id: 'can_quote_prices', label: 'Can AI quote prices?' },
    { id: 'can_provide_advice', label: 'Can AI provide medical/legal advice?', warning: true },
    { id: 'can_escalate_to_human', label: 'Can AI escalate to human staff?' },
    { id: 'can_send_sms', label: 'Can AI send SMS confirmations?' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">AI Permissions & Limits</h2>
        <p className="text-gray-500">Control what your AI agent can and cannot do.</p>
      </div>

      <div className="space-y-4">
        {permissions.map(perm => (
          <div key={perm.id} className={`p-4 rounded-lg ${perm.warning ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                {perm.warning && <AlertTriangle className="w-5 h-5 text-amber-500" />}
                <span className={`font-medium ${perm.warning ? 'text-amber-800' : 'text-gray-900'}`}>
                  {perm.label}
                </span>
                {perm.important && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Important</span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-sm ${data[perm.id] ? 'text-green-600' : 'text-gray-400'}`}>
                  {data[perm.id] ? 'Yes' : 'No'}
                </span>
                <button
                  type="button"
                  onClick={() => onChange(perm.id, !data[perm.id])}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    data[perm.id] ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      data[perm.id] ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </label>
            {perm.warning && (
              <p className="text-sm text-amber-700 mt-2">
                ⚠️ Enabling this may have legal implications. Only enable if you have proper disclaimers.
              </p>
            )}
          </div>
        ))}
      </div>

      {data.can_quote_prices && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Price Disclaimer (required when quoting prices)
          </label>
          <textarea
            value={data.price_disclaimer}
            onChange={(e) => onChange('price_disclaimer', e.target.value)}
            rows={2}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Prices are estimates and may vary based on actual services provided..."
          />
        </div>
      )}
    </div>
  );
};

const Step5AppointmentDetails = ({ data, onChange }) => {
  const addAppointmentType = () => {
    onChange('appointment_types', [...data.appointment_types, { name: '', duration_minutes: 30 }]);
  };

  const updateAppointmentType = (index, field, value) => {
    const updated = [...data.appointment_types];
    updated[index] = { ...updated[index], [field]: value };
    onChange('appointment_types', updated);
  };

  const removeAppointmentType = (index) => {
    onChange('appointment_types', data.appointment_types.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Appointment Details</h2>
        <p className="text-gray-500">Configure how your AI handles appointment bookings.</p>
      </div>

      {/* Appointment Types */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Appointment Types</label>
        <div className="space-y-3">
          {data.appointment_types.map((apt, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <input
                type="text"
                value={apt.name}
                onChange={(e) => updateAppointmentType(idx, 'name', e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g., Regular Checkup"
              />
              <select
                value={apt.duration_minutes}
                onChange={(e) => updateAppointmentType(idx, 'duration_minutes', parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
              {data.appointment_types.length > 1 && (
                <button
                  onClick={() => removeAppointmentType(idx)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addAppointmentType}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            + Add another type
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Max Appointments Per Day
          </label>
          <input
            type="number"
            value={data.max_per_day}
            onChange={(e) => onChange('max_per_day', parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg"
            min={1}
            max={50}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Same-Day Booking?
          </label>
          <select
            value={data.same_day_allowed ? 'yes' : 'no'}
            onChange={(e) => onChange('same_day_allowed', e.target.value === 'yes')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          >
            <option value="yes">Yes, allow same-day</option>
            <option value="no">No, require advance booking</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Booking Confirmation Message
        </label>
        <textarea
          value={data.confirmation_message}
          onChange={(e) => onChange('confirmation_message', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          placeholder="Your appointment is confirmed for [DATE] at [TIME]. We'll see you then!"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Cancellation Policy
        </label>
        <textarea
          value={data.cancellation_policy}
          onChange={(e) => onChange('cancellation_policy', e.target.value)}
          rows={2}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          placeholder="Please cancel at least 24 hours in advance..."
        />
      </div>
    </div>
  );
};

const Step6EmergencyHandling = ({ data, onChange }) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Emergency & Edge Case Handling</h2>
        <p className="text-gray-500">Tell your AI how to handle special situations.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          What counts as an emergency for your business?
        </label>
        <textarea
          value={data.emergency_definition}
          onChange={(e) => onChange('emergency_definition', e.target.value)}
          rows={2}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          placeholder="e.g., Severe tooth pain, bleeding, swelling..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Emergency Routing Number (optional)
        </label>
        <input
          type="tel"
          value={data.emergency_phone}
          onChange={(e) => onChange('emergency_phone', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          placeholder="Number to transfer emergency calls to"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          What should AI say if you're fully booked?
        </label>
        <textarea
          value={data.fully_booked_message}
          onChange={(e) => onChange('fully_booked_message', e.target.value)}
          rows={2}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          placeholder="I'm sorry, we're fully booked today. Would you like me to check availability for tomorrow?"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          What should AI say outside business hours?
        </label>
        <textarea
          value={data.outside_hours_message}
          onChange={(e) => onChange('outside_hours_message', e.target.value)}
          rows={2}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          placeholder="Our office is currently closed. Our hours are Monday-Friday 9AM-5PM..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          What should AI do if unsure how to respond?
        </label>
        <textarea
          value={data.unsure_response}
          onChange={(e) => onChange('unsure_response', e.target.value)}
          rows={2}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          placeholder="I want to make sure I give you accurate information. Can I have someone call you back?"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Fallback Message <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.fallback_message}
          onChange={(e) => onChange('fallback_message', e.target.value)}
          rows={2}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          placeholder="I apologize, but I'm having trouble understanding. Let me transfer you to a staff member."
        />
        <p className="text-xs text-gray-500 mt-1">Used when AI cannot process the request</p>
      </div>
    </div>
  );
};

const Step7BrandVoice = ({ data, toneOptions, onChange }) => {
  const [newPhrase, setNewPhrase] = useState('');

  const addForbiddenPhrase = () => {
    if (newPhrase.trim()) {
      onChange('forbidden_phrases', [...(data.forbidden_phrases || []), newPhrase.trim()]);
      setNewPhrase('');
    }
  };

  const removePhrase = (index) => {
    onChange('forbidden_phrases', data.forbidden_phrases.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Brand Voice & Style</h2>
        <p className="text-gray-500">Define how your AI should sound and communicate.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Desired Tone
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {toneOptions.map(tone => (
            <label
              key={tone.id}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                data.tone === tone.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="tone"
                value={tone.id}
                checked={data.tone === tone.id}
                onChange={(e) => onChange('tone', e.target.value)}
                className="sr-only"
              />
              <div className="font-medium text-gray-900">{tone.label}</div>
              <div className="text-sm text-gray-500">{tone.description}</div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Preferred Greeting Message <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.greeting_message}
          onChange={(e) => onChange('greeting_message', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
          placeholder="Thank you for calling [Business Name]! This is your AI assistant. How may I help you today?"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Forbidden Phrases (optional)
        </label>
        <p className="text-xs text-gray-500 mb-2">Words or phrases your AI should never say</p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newPhrase}
            onChange={(e) => setNewPhrase(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="e.g., 'I don't know'"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addForbiddenPhrase())}
          />
          <button
            onClick={addForbiddenPhrase}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(data.forbidden_phrases || []).map((phrase, idx) => (
            <span key={idx} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm flex items-center gap-1">
              {phrase}
              <button onClick={() => removePhrase(idx)} className="ml-1 hover:text-red-900">×</button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const Step8Review = ({ formData, industries, callObjectives, toneOptions }) => {
  const industry = industries[formData.industry_selection.primary_industry];
  const tone = toneOptions.find(t => t.id === formData.brand_voice.tone);
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review & Submit</h2>
        <p className="text-gray-500">Please review all information before submitting.</p>
      </div>

      {/* Business Profile */}
      <ReviewSection title="Business Profile" icon="🏢">
        <ReviewItem label="Legal Name" value={formData.business_profile.legal_name} />
        <ReviewItem label="Display Name" value={formData.business_profile.display_name} />
        <ReviewItem label="Phone" value={formData.business_profile.primary_phone} />
        <ReviewItem label="Location" value={`${formData.business_profile.city}, ${formData.business_profile.state}`} />
        <ReviewItem label="Timezone" value={formData.business_profile.timezone} />
      </ReviewSection>

      {/* Industry */}
      <ReviewSection title="Industry" icon="🏭">
        <ReviewItem label="Primary" value={industry?.name} />
        <ReviewItem label="Specific Type" value={formData.industry_selection.secondary_industry} />
      </ReviewSection>

      {/* Call Objectives */}
      <ReviewSection title="Call Objectives" icon="📞">
        <div className="flex flex-wrap gap-2">
          {formData.call_objectives.objectives.map(objId => {
            const obj = callObjectives.find(o => o.id === objId);
            return obj && (
              <span key={objId} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                {obj.icon} {obj.label}
              </span>
            );
          })}
        </div>
      </ReviewSection>

      {/* AI Permissions */}
      <ReviewSection title="AI Permissions" icon="🤖">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <ReviewItem label="Book Appointments" value={formData.ai_permissions.can_book_appointments ? '✅ Yes' : '❌ No'} />
          <ReviewItem label="Collect Name" value={formData.ai_permissions.can_collect_name ? '✅ Yes' : '❌ No'} />
          <ReviewItem label="Collect Email" value={formData.ai_permissions.can_collect_email ? '✅ Yes' : '❌ No'} />
          <ReviewItem label="Quote Prices" value={formData.ai_permissions.can_quote_prices ? '✅ Yes' : '❌ No'} />
          <ReviewItem label="Escalate to Human" value={formData.ai_permissions.can_escalate_to_human ? '✅ Yes' : '❌ No'} />
          <ReviewItem label="Send SMS" value={formData.ai_permissions.can_send_sms ? '✅ Yes' : '❌ No'} />
        </div>
      </ReviewSection>

      {/* Brand Voice */}
      <ReviewSection title="Brand Voice" icon="🎤">
        <ReviewItem label="Tone" value={tone?.label} />
        <ReviewItem label="Greeting" value={formData.brand_voice.greeting_message} />
      </ReviewSection>

      {/* Emergency Handling */}
      <ReviewSection title="Emergency Handling" icon="🚨">
        <ReviewItem label="Fallback Message" value={formData.emergency_handling.fallback_message} />
      </ReviewSection>

      {/* Confirmation */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800">Before You Submit</h4>
            <p className="text-sm text-amber-700 mt-1">
              Once submitted, this information will be used to build your AI agent. 
              Changes after submission will require admin approval and may delay activation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReviewSection = ({ title, icon, children }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <div className="bg-gray-50 px-4 py-2 font-medium text-gray-900 flex items-center gap-2">
      <span>{icon}</span>
      {title}
    </div>
    <div className="p-4 space-y-2">
      {children}
    </div>
  </div>
);

const ReviewItem = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-900 font-medium">{value || '—'}</span>
  </div>
);

// Submitted View
const WizardSubmittedView = ({ wizardState }) => (
  <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
    <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Setup Complete!</h1>
      <p className="text-gray-600 mb-6">
        Your AI agent configuration has been submitted. Our team will build your agent within 24-48 hours.
      </p>
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="text-sm text-gray-500 mb-1">Submitted</div>
        <div className="font-medium text-gray-900">
          {new Date(wizardState.submission_timestamp).toLocaleString()}
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
        <Lock className="w-4 h-4" />
        Configuration locked - Contact admin for changes
      </div>
    </div>
  </div>
);

export default OnboardingWizard;
